import { Maybe } from '@codegrade/maybe';
import { expr, itertools, map } from '@codegrade/utils';
/** Export types that are useful for consumers of this package. */
export { AssistantMessageUpdatePart } from './types';
/**
 * Map a Details Content Block to AssistantMessageContentBlock.
 *
 * In the frontend there are two places where this transformation is needed.
 *
 * @param input A details content block.
 * @returns An assistant message content block.
 */
export const mapDetailsContentBlockToAssistantMessageContentBlock = (input) => expr.match(input, 'type', {
    thinking: ({ text: content }) => ({ thinking: { content } }),
    'tool-use-summary': ({ name, summary }) => ({ toolUse: { name, summary } }),
});
/**
 * Aggregator for generated message chunks.
 *
 * This class will make sure output chunks are always concatenated in the
 * correct order, and keeps track of if every part of a message has been
 * received.
 */
export class MessageAggregator {
    message;
    nextExpectedIndex;
    enqueuedChunks;
    content;
    detailsContentBlocks;
    generationId;
    createdAt;
    /**
     * Construct a new Message aggregator.
     *
     * Note: You most likely want to use `create` instead.
     *
     * @param message The message being generated, excluding its data.
     * This is used to maintain the metadata of the message throughout its lifecycle.
     * @param nextExpectedIndex The next index we expect to receive.
     * This ensures that message chunks are processed in the correct order.
     * @param enqueuedChunks Queue for messages with a higher index than the one we need next.
     * This allows for handling out-of-order message chunks by storing them until they can be processed.
     * @param content The current content of the message being generated.
     * This accumulates the text from each processed chunk.
     * @param detailsContentBlocks The content blocks generated by tool uses and thinking process.
     * Like content, the textual content in these also needs to be aggregated from chunks.
     * @param generationId The unique identifier for the message generation process.
     * This is used to ensure that chunks belong to the correct message generation.
     * @param createdAt The time the conversation was started.
     */
    constructor(
    /** The message being generated. */
    message, 
    /** The next index we expect to receive. */
    nextExpectedIndex, 
    /** Queue for messages with a higher index than the one we need next. */
    enqueuedChunks, content, detailsContentBlocks, generationId, createdAt) {
        this.message = message;
        this.nextExpectedIndex = nextExpectedIndex;
        this.enqueuedChunks = enqueuedChunks;
        this.content = content;
        this.detailsContentBlocks = detailsContentBlocks;
        this.generationId = generationId;
        this.createdAt = createdAt;
        Object.freeze(this);
    }
    /** @returns The message identifier, sequence number, unique within one conversation. */
    get messageId() {
        return this.message.messageId;
    }
    /**
     * Create a new message aggregator from an update message.
     *
     * @param assistantId The assistant in which this message is posted.
     * @param conversationId The conversation in which this message is posted.
     * @param update The "generation-started" update message.
     * @returns A new `MessageAggregator` instance.
     */
    static create(assistantId, conversationId, update) {
        // Always create an empty aggregator, regardless of the update type.
        // The initial chunk will be processed separately via processChunk() if needed.
        return new MessageAggregator(Object.freeze({
            assistantId,
            conversationId,
            messageId: update.messageId.toString(),
        }), 0, new map.ReadonlyMaybeMap(), '', Maybe.empty(), update.generationId, Maybe.empty());
    }
    /**
     * This function allows updating the start time of the conversation and is
     * used in instances where the start message arrives later.
     *
     * @param createdAt The start of the conversation with the assistant.
     * @returns The updated assistant.
     */
    processStart(createdAt) {
        return new MessageAggregator(this.message, this.nextExpectedIndex, this.enqueuedChunks, this.content, this.detailsContentBlocks, this.generationId, Maybe.of(createdAt.toDate()));
    }
    /**
     * Apply a single chunk to the aggregator, updating content and detail blocks.
     *
     * This method assumes the chunk is the next expected chunk (index validation already done).
     *
     * @param chunk The chunk to apply.
     * @returns A new MessageAggregator with the chunk applied.
     */
    applyChunk(chunk) {
        return expr.match(chunk, 'type', {
            'output-chunk': ({ text }) => new MessageAggregator(this.message, this.nextExpectedIndex + 1, this.enqueuedChunks, this.content + text, this.detailsContentBlocks, this.generationId, this.createdAt),
            'thinking-text-chunk': ({ text }) => {
                // Check if last detail block is thinking, append to it. Otherwise create new block.
                const blocks = this.detailsContentBlocks.orDefault([]);
                const lastBlock = blocks[blocks.length - 1];
                let updatedBlocks;
                if (lastBlock?.type === 'thinking') {
                    // Append to existing thinking block
                    updatedBlocks = [
                        ...blocks.slice(0, -1),
                        { type: 'thinking', text: lastBlock.text + text },
                    ];
                }
                else {
                    // Create new thinking block
                    updatedBlocks = [...blocks, { type: 'thinking', text }];
                }
                return new MessageAggregator(this.message, this.nextExpectedIndex + 1, this.enqueuedChunks, this.content, Maybe.of(updatedBlocks), this.generationId, this.createdAt);
            },
            'tool-use-summary': ({ name, summary }) => {
                // Add tool use block
                const blocks = this.detailsContentBlocks.orDefault([]);
                const updatedBlocks = [
                    ...blocks,
                    { type: 'tool-use-summary', name, summary },
                ];
                return new MessageAggregator(this.message, this.nextExpectedIndex + 1, this.enqueuedChunks, this.content, Maybe.of(updatedBlocks), this.generationId, this.createdAt);
            },
        });
    }
    /**
     * Process an output chunk.
     *
     * @param update The "output-chunk" websocket update.
     * @returns `Nothing` if the received update could not be applied yet
     * because we haven't received an earlier chunk yet. Otherwise the updated
     * message wrapped in a `Just`.
     */
    processChunk(update) {
        if (update.generationId !== this.generationId) {
            return this;
        }
        if (update.index !== this.nextExpectedIndex) {
            return new MessageAggregator(this.message, this.nextExpectedIndex, new map.ReadonlyMaybeMap(itertools.chain([
                this.enqueuedChunks.entries(),
                [[update.index, update]],
            ])), this.content, this.detailsContentBlocks, this.generationId, this.createdAt);
        }
        // Apply the current chunk
        let result = this.applyChunk(update);
        // Process all enqueued chunks in order
        for (let chunk = result.enqueuedChunks.get(result.nextExpectedIndex); chunk.isJust(); chunk = result.enqueuedChunks.get(result.nextExpectedIndex)) {
            result = result.applyChunk(chunk.value);
        }
        return result;
    }
    /**
     * Finalize the message being generated.
     *
     * @param update The "generation-ended" websocket update.
     * @returns `Nothing` if not all chunks of the message have been received.
     * Otherwise the final message wrapped in a `Just`.
     */
    finalize(update) {
        if (update.totalChunks !== this.nextExpectedIndex) {
            return Maybe.empty();
        }
        return this.createdAt.map(createdDate => ({
            ...this.message,
            createdAt: createdDate,
            data: {
                assistant: {
                    content: { text: this.content },
                    detailsSummary: update.detailsSummary,
                    stopReason: expr.match(update.stopReason, 'reason', {
                        end_turn: () => ({
                            endTurn: {},
                        }),
                        max_tokens: ({ maxTokens }) => ({
                            maxTokens: { maxTokens },
                        }),
                    }),
                },
            },
            detailsBlock: this.detailsContentBlocks,
        }));
    }
}
